import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, switchMap, throwError } from 'rxjs';
import { HttpBodyResponse } from 'src/app/core/api-dto/interface/api-http-response.interface';
import { Logger } from 'src/app/core/interfaces/Logger';
import { buildHttpParams } from 'src/app/shared/utils/http.utils';
import { environment } from 'src/environments/environment';
import { Ticket } from '../../domain/model/ticket.model';
import { IApiTicket, IApiTicketGetAllQueryParams, IApiTicketGetBySearchQueryParams, IApiTicketV2 } from '../interfaces/ticket-api.interface';
import { TicketFactoryService } from './ticket-factory.service';
import { TicketMapper } from '../mapper/ticket.mapper';
import { OrganizationRepositoryAdapter } from 'src/app/core/entities/organization/infrastructure/adapters/organization-repository.adapter';

@Injectable({ providedIn: 'root' })
export class TicketApiService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(Logger);
  private readonly ticketFactoryService = inject(TicketFactoryService);
  private readonly organizationService = inject(OrganizationRepositoryAdapter);
  private readonly invoiceUrl = `${environment.apiURL}/api/v2/invoices`;
  private readonly ticketUrl = `${environment.apiURL}/tickets`;


  getAll(params?: IApiTicketGetAllQueryParams): Observable<Ticket[]> {
    return this.get<IApiTicket[]>(this.ticketUrl, ``, params as unknown as Record<string, unknown>).pipe(
      switchMap((apiTickets) => this.ticketFactoryService.createMany(apiTickets))
    );
  }

  getById(id: string): Observable<Ticket> {
    return this.get<IApiTicket>(this.ticketUrl, id).pipe(
      switchMap((apiTicket) => this.ticketFactoryService.createOne(apiTicket, true))
    );
  }

  getBySearch(params?: IApiTicketGetBySearchQueryParams): Observable<Ticket[]> {
    const organization = this.organizationService.getOrganizationFromCache();

    return this.get<IApiTicketV2[]>(this.invoiceUrl, `search`, params as unknown as Record<string, unknown>).pipe(
      map((apiTickets) => apiTickets.map(ticket => ({
        ...TicketMapper.toV1JSON(ticket),
        cif: ticket.cif || organization?.cif || '',
        organization_name: organization?.organization_name || ticket.organizationName || ''
      }))),
      switchMap((apiTickets) => this.ticketFactoryService.createMany(apiTickets))
    );
  }

  getByIds(ids: string[]): Observable<Ticket[]> {
    return this.get<IApiTicket[]>(this.ticketUrl, `not-pending/by-ids`, { ids: ids.join(',') }).pipe(
      switchMap((apiTickets) => this.ticketFactoryService.createMany(apiTickets))
    );
  }

  /**
   * Generic GET method that automatically maps all query parameters
   * Follows Open/Closed Principle - open for extension, closed for modification
   */
  private get<T>(
    baseUrl: string,
    endpoint = '',
    queryParams?: Record<string, unknown>
  ): Observable<T> {
    const params = buildHttpParams(queryParams);

    return this.makeRequest<T>(
      this.http.get<HttpBodyResponse<T>>(
        `${baseUrl}/${endpoint}`.replace(/\/$/, ''),
        { params }
      )
    );
  }


  private makeRequest<T>(
    request: Observable<HttpBodyResponse<T>>
  ): Observable<T> {
    return request.pipe(
      map((response) => response.data!),
      catchError((error) => {
        this.logger.error(
          new Error('[TicketApiService] Operation failed'),
          error
        );

        return throwError(() => error);
      })
    );
  }
}
